/*
 * cl-switcher.js
 * --------------
 * Runtime for the self-contained Combined-Listing switcher (Habitat 1.6.1, no
 * native CL support). Pairs with snippets/cl-switcher.liquid.
 *
 * Problem: a Combined Listing's parent groups several CHILD products. Each value
 * of the "config" option (e.g. Configuration) belongs to a different child, and
 * children can have DIFFERENT option sets (some have Orientation, some don't).
 * The theme renders the parent as one flat multi-variant product, so it can't
 * resolve a variant when option sets differ and freezes on the default child
 * (URL/price/image stuck).
 *
 * Fix: this runtime is the SINGLE handler for every option change inside the
 * product picker (`<variant-selects>`). On any change it reads all currently
 * selected option values and decides the target:
 *
 *   1. CONFIG change — the changed combined-listing option value maps (via
 *      product_url) to a DIFFERENT child product: fetch that child's main-product
 *      section, extract its `.thb-product-detail`, and replace the live
 *      `.thb-product-detail` innerHTML in place. The child's section carries its
 *      OWN correct picker (incl. Orientation), price, gallery and add-to-cart
 *      form, so mixed-option families work.
 *
 *   2. WITHIN-PRODUCT change — Color/Orientation/etc. on the CURRENT child: the
 *      theme's native variant handling is broken on these combined pages (the
 *      mixed-in CL option breaks its variant matching, so image + price never
 *      swap). We resolve the newly-selected variant from the child's inline
 *      variant JSON, then section-render the CURRENT product at that variant
 *      (`?variant=<id>&section_id=<id>`) and swap `.thb-product-detail`, which
 *      brings the correct price + gallery slide.
 *
 * Shopify custom elements (product-slider, variant-selects) run
 * connectedCallback() when inserted, so the gallery + picker re-initialize
 * themselves after every swap.
 *
 * IMPORTANT: the picker (`<variant-selects>` and its inline variant JSON) lives
 * INSIDE `.thb-product-detail`, so a config swap replaces it with a different
 * child's picker. We therefore read the variant JSON and the current product URL
 * LIVE from the DOM on every change rather than caching them at init.
 *
 * Defensive: any failure falls back to plain navigation (window.location = url),
 * so the user always reaches the correct product, and the in-flight guard is
 * reset on every branch so the page never freezes.
 */
(function () {
  'use strict';

  var DETAIL_SELECTOR = '.thb-product-detail';
  var DATA_ID = 'cl-switcher-data';
  var BOUND_FLAG = 'clSwitcherBound';

  function readData() {
    var el = document.getElementById(DATA_ID);
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  // Normalize a URL to its pathname so query params / origin differences don't
  // cause false "different product" or false "same product" decisions.
  function pathOf(url) {
    try {
      return new URL(url, window.location.origin).pathname.replace(/\/+$/, '');
    } catch (e) {
      // Best-effort: strip query/hash and trailing slash from a raw string.
      return String(url || '')
        .split('#')[0]
        .split('?')[0]
        .replace(/\/+$/, '');
    }
  }

  var data = readData();
  if (!data || !data.values || !data.values.length || !data.optionName) {
    return; // not a combined listing (or malformed) -> do nothing.
  }

  // Live runtime state. We track the current URL in JS rather than re-reading the
  // data node, because that node may live inside the swapped region and a
  // re-inserted <script> would not re-run anyway.
  var sectionId = data.sectionId;
  var optionName = data.optionName;
  var currentPath = pathOf(data.currentUrl);

  // Build a lookup from option-value title -> child url.
  function buildMap(values) {
    var map = {};
    for (var i = 0; i < values.length; i++) {
      if (values[i] && values[i].value != null) {
        map[String(values[i].value)] = values[i].url;
      }
    }
    return map;
  }
  var valueToUrl = buildMap(data.values);

  // Does this control belong to the combined-listing option?
  // Radios use name="Configuration"; selects use name="options[Configuration]".
  function isClOptionControl(name) {
    if (!name) return false;
    return name === optionName || name === 'options[' + optionName + ']';
  }

  // Pull the selected config value out of whatever control fired.
  function selectedValueFrom(target) {
    if (!target) return null;
    if (target.tagName === 'SELECT') return target.value;
    if (target.type === 'radio' && target.checked) return target.value;
    return null;
  }

  function navigateFallback(url) {
    window.location = url;
  }

  // Append a query param to a URL string (handles ?/& and existing keys cheaply;
  // good enough for our internal product URLs).
  function withParam(url, key, val) {
    return (
      url +
      (url.indexOf('?') === -1 ? '?' : '&') +
      key +
      '=' +
      encodeURIComponent(val)
    );
  }

  // The live picker element for the currently-rendered child. Lives INSIDE
  // .thb-product-detail, so it changes identity after every config swap.
  function liveVariantSelects() {
    return document.querySelector('variant-selects');
  }

  // Current product URL, read LIVE: prefer the live picker's data-url, fall back
  // to our maintained currentPath. This stays correct after a config swap.
  function currentProductUrl() {
    var vs = liveVariantSelects();
    var u = vs && vs.getAttribute('data-url');
    return u || currentPath;
  }

  // Parse the current child's inline variant JSON (array of variant objects).
  function liveVariants() {
    var vs = liveVariantSelects();
    if (!vs) return null;
    var script = vs.querySelector('script[type="application/json"]');
    if (!script) return null;
    try {
      var arr = JSON.parse(script.textContent);
      return Array.isArray(arr) ? arr : null;
    } catch (e) {
      return null;
    }
  }

  // Read every currently-selected option value from the live picker as a flat
  // list of values (we don't need the names: variant.options is positional and
  // its value vocabulary is distinct across options on these products).
  function selectedOptionValues() {
    var vs = liveVariantSelects();
    if (!vs) return [];
    var out = [];
    // Radios: one checked per option group.
    var radios = vs.querySelectorAll('input[type="radio"]:checked');
    for (var i = 0; i < radios.length; i++) out.push(radios[i].value);
    // Selects: each contributes its current value.
    var selects = vs.querySelectorAll('select');
    for (var j = 0; j < selects.length; j++) {
      if (selects[j].value != null && selects[j].value !== '') {
        out.push(selects[j].value);
      }
    }
    return out;
  }

  // Find the variant whose option values exactly match the selected set. The CL
  // config value is identical across a child's variants, so matching all
  // selected values (Config + Color + Orientation) resolves a single variant.
  // Returns the variant id, or null if none/ambiguous matches.
  function resolveVariant(variants, selectedValues) {
    if (!variants || !selectedValues.length) return null;
    var found = null;
    for (var i = 0; i < variants.length; i++) {
      var opts = variants[i] && variants[i].options;
      if (!opts || opts.length !== selectedValues.length) continue;
      var ok = true;
      for (var k = 0; k < selectedValues.length; k++) {
        if (opts.indexOf(selectedValues[k]) === -1) {
          ok = false;
          break;
        }
      }
      if (ok) {
        if (found != null && found.id !== variants[i].id) return null; // ambiguous
        found = variants[i];
      }
    }
    return found;
  }

  // After a within-product swap, jump the gallery to the selected variant's OWN
  // image. The section's lead slide is shared across a product's variants (e.g.
  // Left/Right Facing share the same first photo), so without this the visible
  // image wouldn't change. Waits for Flickity to (re)initialize after the swap.
  function selectGallerySlide(detailEl, mediaId) {
    if (!mediaId) return;
    var slider = detailEl.querySelector('.product-images');
    if (!slider) return;
    var slideSel = '[id="Slide-' + sectionId + '-' + mediaId + '"]';
    var attempts = 0;
    function tick() {
      attempts++;
      var flkty =
        typeof Flickity !== 'undefined' && Flickity.data ? Flickity.data(slider) : null;
      var slide = slider.querySelector(slideSel);
      if (flkty && slide) {
        try {
          flkty.selectCell(slide);
        } catch (e) {}
        return;
      }
      if (attempts < 30) window.requestAnimationFrame(tick);
    }
    window.requestAnimationFrame(tick);
  }

  // Hide option values that don't exist for the CURRENT child. The picker renders
  // the UNION of all values across the combined listing (e.g. Orientation =
  // Right/Left/Symmetrical), but a given child may only offer some. We never touch
  // the combined-listing config option — its other values are valid siblings
  // reachable via product_url.
  function pruneUnavailableOptions() {
    var vs = liveVariantSelects();
    var variants = liveVariants();
    if (!vs || !variants) return;
    var available = {};
    for (var i = 0; i < variants.length; i++) {
      var opts = variants[i] && variants[i].options;
      if (!opts) continue;
      for (var k = 0; k < opts.length; k++) available[opts[k]] = true;
    }
    var radios = vs.querySelectorAll('input[type="radio"]');
    for (var r = 0; r < radios.length; r++) {
      var radio = radios[r];
      if (isClOptionControl(radio.name)) continue; // leave the config option intact
      var ok = available[radio.value] === true;
      radio.disabled = !ok;
      var label = vs.querySelector('label[for="' + radio.id + '"]');
      if (label) label.style.display = ok ? '' : 'none';
    }
  }

  var swapInFlight = false;

  // Core swap: section-render `url` (already carrying any ?variant=) and replace
  // the live .thb-product-detail innerHTML. `historyUrl` is pushed/replaced into
  // the address bar; `usePush` chooses pushState (config->child) vs replaceState
  // (within-product variant). Custom elements re-init via connectedCallback.
  function swapDetail(url, historyUrl, usePush, mediaId) {
    if (swapInFlight) return;
    var liveDetail = document.querySelector(DETAIL_SELECTOR);
    if (!liveDetail) {
      navigateFallback(historyUrl || url);
      return;
    }
    swapInFlight = true;

    // Primary: Section Rendering API for the product's main-product section.
    var sectionUrl = withParam(url, 'section_id', sectionId);

    fetchDetail(sectionUrl)
      .then(function (detailHtml) {
        if (detailHtml) return detailHtml;
        // Fallback: fetch the full page and extract the detail container.
        return fetchDetail(url);
      })
      .then(function (detailHtml) {
        if (!detailHtml) {
          throw new Error('no .thb-product-detail in response');
        }
        liveDetail.innerHTML = detailHtml;
        currentPath = pathOf(url);

        // Jump the gallery to the variant's own image (within-product changes only;
        // config swaps pass no mediaId since the child's lead slide is already correct).
        selectGallerySlide(liveDetail, mediaId);

        // Hide option values not offered by the now-current child.
        pruneUnavailableOptions();

        // Update browser URL so refresh / share lands on the right state.
        try {
          if (usePush) {
            window.history.pushState({ clSwitcher: true }, '', historyUrl);
          } else {
            window.history.replaceState({ clSwitcher: true }, '', historyUrl);
          }
        } catch (e) {
          /* can throw on cross-origin; ignore, swap already done */
        }

        // Update document title from the freshly-swapped picker if available.
        updateTitleFrom(liveDetail);

        swapInFlight = false;
      })
      .catch(function () {
        swapInFlight = false;
        navigateFallback(historyUrl || url); // always reach the right state.
      });
  }

  // CONFIG change: swap to a DIFFERENT child product (pushState the child URL).
  function swapToChild(url) {
    swapDetail(url, url, true);
  }

  // WITHIN-PRODUCT change: re-render the CURRENT product at `variantId`
  // (replaceState a ?variant= URL so color/orientation clicks don't spam history).
  function swapToVariant(productUrl, variantId, mediaId) {
    var variantUrl = withParam(productUrl, 'variant', variantId);
    swapDetail(variantUrl, variantUrl, false, mediaId);
  }

  // Fetch a URL and return the innerHTML of its `.thb-product-detail`, or null.
  function fetchDetail(fetchUrl) {
    return fetch(fetchUrl, { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (resp) {
        if (!resp.ok) throw new Error('HTTP ' + resp.status);
        return resp.text();
      })
      .then(function (text) {
        var doc = new DOMParser().parseFromString(text, 'text/html');
        var detail = doc.querySelector(DETAIL_SELECTOR);
        return detail ? detail.innerHTML : null;
      });
  }

  function updateTitleFrom(detailEl) {
    var titleEl = detailEl.querySelector('.product-title');
    if (titleEl && titleEl.textContent) {
      var t = titleEl.textContent.trim();
      if (t) document.title = t;
    }
  }

  // Single dispatcher for ALL option changes inside the product picker.
  function handleChange(e) {
    var target = e.target;
    // Scope to the live picker by DOM origin, NOT by name: Color/Orientation
    // names aren't in cl-switcher-data, and this keeps unrelated page controls
    // (newsletter, quantity, search...) from triggering spurious resolution.
    if (!target || typeof target.closest !== 'function') return;
    if (!target.closest('variant-selects')) return;
    if (swapInFlight) return;

    // (1) CONFIG change: the changed CL-option control maps to a DIFFERENT child.
    if (isClOptionControl(target.name)) {
      var value = selectedValueFrom(target);
      if (value != null) {
        var url = valueToUrl[String(value)];
        if (url && pathOf(url) !== currentPath) {
          swapToChild(url);
          return;
        }
        // Same-product CL value (e.g. re-selecting current config): fall through
        // to within-product resolution, which harmlessly re-resolves it.
      }
    }

    // (2) WITHIN-PRODUCT change: resolve the newly-selected variant of the
    // CURRENT product and section-render it. Read variants + URL LIVE so this
    // stays correct after a prior config swap replaced the picker.
    var variants = liveVariants();
    var variant = resolveVariant(variants, selectedOptionValues());
    if (!variant) return; // no/ambiguous match -> let the theme handle it.

    var mediaId = variant.featured_media && variant.featured_media.id;
    swapToVariant(currentProductUrl(), variant.id, mediaId);
  }

  // Delegate on a stable ancestor that is NOT replaced. The data/script render
  // outside .thb-product-detail, but to be safe we delegate on document so the
  // listener survives every innerHTML swap and re-binds nothing.
  function bind() {
    if (document[BOUND_FLAG]) return;
    document[BOUND_FLAG] = true;
    document.addEventListener('change', handleChange, false);
  }

  bind();
  pruneUnavailableOptions(); // prune on initial load too
})();
